import { useMemo } from 'react';

import { Loader, LoaderModeType } from 'components/Loader';

import {
    GetSupplyOrderSupplierProductsStockVariationQuery,
    useGetSupplyOrderSupplierProductsStockVariationQuery,
} from 'data/__generated__';

import { ProductsInfoStockVariation } from '../shared/stockVariation/ProductsInfoStockVariation';
import { StockVariationProductInfo } from '../shared/types';
import { SupplyOrderDetails } from './supplyOrderFormValues';

type SupplyOrderStockVariation =
    GetSupplyOrderSupplierProductsStockVariationQuery['supplyOrderSupplierProductsStockVariation']['supplyOrderStockVariations'][number];

export type SupplyOrderStockVariations = {
    byProductId: Map<string, SupplyOrderStockVariation>;
    loading: boolean;
    hasError: boolean;
};

/**
 * Les variations de stock de tout le catalogue du fournisseur, indexées par produit.
 *
 * Même requête et mêmes variables que la section « Prévisions » : Apollo ne la joue qu'une fois pour
 * toute la fiche, quel que soit le nombre de tableaux qui l'affichent.
 */
export function useSupplyOrderStockVariations(supplyOrderId: string): SupplyOrderStockVariations {
    const { data, loading, error } = useGetSupplyOrderSupplierProductsStockVariationQuery({
        variables: { supplyOrderId },
    });

    const byProductId = useMemo(
        () =>
            new Map(
                (data?.supplyOrderSupplierProductsStockVariation.supplyOrderStockVariations ?? []).map(
                    (stockVariation) => [stockVariation.productId, stockVariation],
                ),
            ),
        [data],
    );

    return { byProductId, loading, hasError: !!error };
}

/** L'aperçu jour par jour d'une ligne ; un clic ouvre le détail des variations. */
export const StockVariationPreviewCell = ({
    stockVariations,
    productId,
    productInfo,
    stock,
    orderingTimes,
    shouldDisplayTooltip,
}: {
    stockVariations: SupplyOrderStockVariations;
    productId: string;
    productInfo: StockVariationProductInfo;
    stock: number;
    orderingTimes: SupplyOrderDetails['supplier']['orderingTimes'];
    shouldDisplayTooltip: boolean;
}) => {
    if (stockVariations.loading) {
        return <Loader size="22px" mode={LoaderModeType.Spin} />;
    }

    if (stockVariations.hasError) {
        return <span className="text-xs text-destructive">Erreur de chargement</span>;
    }

    const productStockVariation = stockVariations.byProductId.get(productId);

    if (!productStockVariation) {
        return <span className="text-xs text-muted-foreground">Aucun mouvement prévu</span>;
    }

    return (
        <ProductsInfoStockVariation
            isComputed={productStockVariation.isComputed}
            orderingTimes={orderingTimes}
            productInfo={productInfo}
            productStockVariation={productStockVariation}
            stock={stock}
            startDate={productStockVariation.orderDate}
            shouldDisplayTooltip={shouldDisplayTooltip}
        />
    );
};
